import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SharedModule } from '@shared/shared.module';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzCascaderModule } from 'ng-zorro-antd/cascader';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { AuthService } from 'src/app/Auth/AuthService';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { HasUnsavedChanges } from 'src/app/Auth/unsaved-changes.guard';
import { ImageCropperComponent, ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { SecureStorageService } from '@services/secure-storage.service';
import { CompanionCardComponent } from '../companion/companion-card.component';
import { hasCompanionAccess } from 'src/app/utils/companion-access.util';
import { TranslateService } from '@ngx-translate/core';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import * as lpn from 'google-libphonenumber';
import { take } from 'rxjs/operators';
import { CountryService } from '@services/country.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzCascaderModule,
    NzDrawerModule,
    SharedModule,
    NzModalModule,
    ImageCropperComponent,
    CompanionCardComponent,
    NgxIntlTelInputModule,
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, HasUnsavedChanges {
  @ViewChild('exampleModal') exampleModal!: ElementRef;
  pagesService = inject(PagesService);
  private modal = inject(NzModalService);
  profileForm: FormGroup;
  resetPasswordForm: FormGroup;
  sharedService = inject(SharedService);
  secureStorage = inject(SecureStorageService);
  translateService = inject(TranslateService);
  private countryService = inject(CountryService);
  currentImage: any;
  companyData: any;
  imageSrc: any;
  selectedValue = 'User';
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  isPasswordVisible = false;
  hasCompanionAccess: boolean = false;

  // Image cropper variables
  cropperModalVisible = false;
  imageChangedEvent: Event | null = null;
  /** A picture dropped on the drawer's photo area; the cropper reads it instead of a change event. */
  droppedImageFile: File | undefined;
  isPhotoDragOver = false;
  croppedImage: string = '';

  // Edit mode variables — isEditMode is the Edit Profile drawer being open
  isEditMode = false;
  userName: string = '';
  userEmail: string = '';
  userPhone: string = '';
  businessName: string = '';
  businessNameArabic: string = '';
  editableBusinessNameArabic: string = '';
  editableName: string = '';
  editableBusinessName: string = '';
  editablePhone: string = '';
  // Phone widget state for the drawer: the country picker and the number typed against it.
  readonly SearchCountryField = SearchCountryField;
  readonly PhoneNumberFormat = PhoneNumberFormat;
  editPhoneCountry: CountryISO = CountryISO.Pakistan;
  editPhoneInput: any = '';
  private editPhoneTyped = '';
  epPhoneTouched = false;
  businessDescription: string = '';
  originalBusinessDescription: string = '';
  isDescriptionChanged: boolean = false;
  businessSector: any[] = [];
  originalBusinessSector: any[] = [];
  sectorsList: any[] = [];
  sectorsTree: any[] = [];
  // Keyed by the id as a string, so numeric and string ids from the API look up alike.
  private sectorNameMap = new Map<string, string>();
  /** The sector value exactly as the API sent it, so paths can be rebuilt once the tree loads. */
  private rawSectorData: any = null;
  sectorMenuStyle: { [key: string]: string } = {};
  @ViewChild('businessSectorCascader', { read: ElementRef })
  businessSectorCascader?: ElementRef<HTMLElement>;

  // Password strength tracking - initially all false
  passwordStrength = {
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false,
    minLength: false,
  };

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.profileForm = this.fb.group({
      business_logo: [''],
      name: ['', Validators.required],
      address: [''],
      userDetail: this.fb.array([this.createUserDetail()]),
      password: [
        '',
        [
          Validators.minLength(8),
          Validators.pattern(
            /^(?=.*[A-Za-z])(?=.*[^A-Za-z0-9\s])\S{8,64}$/
          ),
        ],
      ],
    });

    // ✅ Enhanced reset password form with custom validator
    this.resetPasswordForm = this.fb.group(
      {
        oldPassword: ['', Validators.required],
        new_password: ['', [Validators.required]],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator } // ✅ Add custom validator
    );
  }
  profileImage: any

  // Image Modal Properties
  imageModalVisible: boolean = false;
  imageModalImages: string[] = [];
  imageModalCurrentIndex: number = 0;

  ngOnInit() {
    // Only the plan decides; the user record's companion flags are not a grant.
    this.hasCompanionAccess = this.secureStorage.getItem<boolean>('hasCompanionAccess') || false;

    this.sharedService.updatedUserData.subscribe((planData: any) => {
      if (planData && typeof planData === 'object') {
        const hasAccess = hasCompanionAccess(planData);
        this.hasCompanionAccess = hasAccess;
        void this.secureStorage.setItem('hasCompanionAccess', hasAccess);
      }
    });

    // Get company details which also loads user data from _user object
    this.getCompanyDetail();
    this.loadSectors();

    this.sharedService.profileImage$.subscribe((img) => {
      if (img) {
        this.profileImage = img;
      }
    });

    // Initialize password strength as false (unchecked)
    this.initializePasswordStrength();
  }

  // Load user data from API (called after getCompanyDetail)
  loadUserData() {
    // This will be populated from API response in getCompanyDetail
    // Keeping method for backward compatibility
  }

  get isArabic(): boolean {
    return localStorage.getItem('lang') === 'ar';
  }

  get displayBusinessName(): string {
    const language = this.translateService.currentLang || localStorage.getItem('lang');
    return language === 'ar' && this.businessNameArabic.trim()
      ? this.businessNameArabic
      : this.businessName;
  }

  private get arabicBusinessNameStorageKey(): string | null {
    const id = this.companyData?.id ?? this.companyData?.company_as_client_0_id ?? this.companyData?.company_id;
    return id == null ? null : `company-arabic-name:${id}`;
  }

  // Open the Edit Profile drawer with the saved values
  openEditDrawer() {
    this.editableName = this.userName;
    this.editableBusinessName = this.businessName;
    this.editableBusinessNameArabic = this.businessNameArabic;
    this.editablePhone = this.userPhone;
    this.loadEditPhone();
    this.cancelCompanyNameEdit();
    this.isEditMode = true;
  }

  /** Opens the phone field on the saved number's own country, or the visitor's country when empty. */
  private loadEditPhone(): void {
    const saved = this.splitSavedPhone(this.userPhone);
    this.editPhoneTyped = saved.number;
    this.editPhoneInput = saved.number;
    this.epPhoneTouched = false;
    if (saved.iso) {
      this.editPhoneCountry = saved.iso;
      return;
    }
    this.countryService.getPhoneCountry().pipe(take(1)).subscribe((iso) => {
      // Never switch the country under a number the user has already started typing.
      if (iso && !this.editPhoneTyped.trim()) this.editPhoneCountry = iso;
    });
  }

  private splitSavedPhone(raw: string): { iso: CountryISO | null; number: string } {
    let value = String(raw || '').replace(/[\s()-]/g, '');
    if (!value) return { iso: null, number: '' };
    if (value.startsWith('00')) value = '+' + value.slice(2);
    if (!value.startsWith('+')) value = '+' + value;
    try {
      const util = lpn.PhoneNumberUtil.getInstance();
      const parsed = util.parse(value);
      // A number libphonenumber can't place (e.g. a 555 test number) still carries its dialing code.
      const region: string | undefined =
        util.getRegionCodeForNumber(parsed) || util.getRegionCodeForCountryCode(parsed.getCountryCode());
      const iso = region && region !== 'ZZ' ? (region.toLowerCase() as CountryISO) : null;
      if (!iso || !Object.values(CountryISO).includes(iso)) return { iso: null, number: raw };
      return { iso, number: String(parsed.getNationalNumber() ?? '') };
    } catch {
      return { iso: null, number: raw };
    }
  }

  /** The widget hands back an object; the rest of the page keeps working with the E.164 string. */
  onEditPhoneChange(value: any): void {
    const typed = typeof value === 'string' ? value : value?.number || '';
    this.editPhoneTyped = String(typed);
    if (!this.editPhoneTyped.trim()) {
      this.editablePhone = '';
      return;
    }
    this.editablePhone = (typeof value === 'object' && value?.e164Number) || this.editPhoneTyped;
  }

  // Phone is optional; when given it must be a valid number for the country picked beside it.
  private normalizePhone(value: string): string {
    return (value || '').replace(/[\s-]/g, '');
  }

  get isEditablePhoneValid(): boolean {
    const phone = this.normalizePhone(this.editablePhone);
    if (!phone) return true;
    try {
      const util = lpn.PhoneNumberUtil.getInstance();
      return util.isValidNumber(util.parse(phone.startsWith('+') ? phone : '+' + phone));
    } catch {
      return false;
    }
  }

  // Close the drawer and throw away anything not saved
  cancelProfileEdit() {
    const imageChanged = this.profileForm.get('business_logo')?.dirty;
    this.isEditMode = false;
    this.isPhotoDragOver = false;
    this.editableName = this.userName;
    this.editableBusinessName = this.businessName;
    this.editableBusinessNameArabic = this.businessNameArabic;
    this.editablePhone = this.userPhone;
    this.cancelCompanyNameEdit();
    // Only an unsaved picture needs the server copy back; the text fields were reset above.
    if (imageChanged) {
      this.profileForm.get('business_logo')?.markAsPristine();
      this.getCompanyDetail();
    }
  }

  // Track business info changes
  onCompanyNameChange() {
    const sectorChanged = JSON.stringify(this.businessSector) !== JSON.stringify(this.originalBusinessSector);
    this.isDescriptionChanged =
      this.businessDescription !== this.originalBusinessDescription || sectorChanged;
  }

  // Cancel company name edit
  cancelCompanyNameEdit() {
    this.businessDescription = this.originalBusinessDescription;
    this.businessSector = [...this.originalBusinessSector];
    this.isDescriptionChanged = false;
  }

  // Save changes with API call — one save for the whole page: name, business name,
  // description and sector together.
  saveProfileChanges() {
    if (!this.editableName.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.name-empty'));
      return;
    }
    if (!this.editableBusinessName.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.business-name-empty'));
      return;
    }
    if (!this.isEditablePhoneValid) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.phone-invalid'));
      return;
    }
    if (!this.businessDescription.trim() && this.originalBusinessDescription.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.business-desc-empty'));
      return;
    }

    this.sharedService.showLoader();

    // Prepare payload for company profile update
    const companyId = this.companyData?.id ?? this.companyData?.company_as_client_0_id ?? this.companyData?.company_id;
    if (companyId) {
      const userDetails = this.authService.getUserDetails() || {};
      const phoneNumber = this.normalizePhone(this.editablePhone);
      const imageChanged = this.profileForm.get('business_logo')?.dirty;
      const businessLogoPayload = imageChanged ? this.prepareBusinessLogoPayload() : undefined;
      const userId = this.companyData?._user?.id ?? this.companyData?.user?.id ?? this.companyData?.user_id ?? userDetails?.id;
      const userEmail = this.companyData?._user?.email ?? this.companyData?.user?.email ?? this.userEmail ?? userDetails?.email;

      const payload: any = {
        name: (this.editableBusinessName || this.businessName).trim(),
        details: this.businessDescription,
        address: this.businessDescription,
        description: this.businessDescription,
        sector_id: this.extractSectorIds(this.businessSector),
        business_sector: this.extractSectorIds(this.businessSector),
        // Only sent when filled in, so an empty field never wipes the saved number.
        ...(phoneNumber ? { phone_number: phoneNumber } : {}),
        user_name: this.editableName.trim(),
        _user: {
          ...(userId ? { id: userId } : {}),
          name: this.editableName.trim(),
          ...(userEmail ? { email: userEmail } : {}),
          ...(phoneNumber ? { phone_number: phoneNumber } : {}),
        },
        company_as_client_0_id: companyId,
        id: companyId,
      };

      if (imageChanged) {
        payload.business_logo = businessLogoPayload;
      }

      this.pagesService
        .patch('SET_COMPANY_DETAILS', companyId, payload)
        .subscribe({
          next: async (response) => {
            const arabicNameKey = this.arabicBusinessNameStorageKey;
            if (arabicNameKey) {
              try {
                await this.secureStorage.setItem(arabicNameKey, this.editableBusinessNameArabic.trim());
                this.businessNameArabic = this.editableBusinessNameArabic.trim();
              } catch {
                this.sharedService.createNotification('warning', this.translateService.instant('profile-page.business-name-arabic-save-failed'));
              }
            }
            this.sharedService.hideLoader();

            // Update local variables
            this.userName = this.editableName;
            this.userPhone = this.editablePhone;
            if (this.editableBusinessName) {
              this.businessName = this.editableBusinessName;
            }

            // Update user details in storage
            const userDetails = this.authService.getUserDetails() || {};
            userDetails.name = this.userName;
            if (phoneNumber) {
              userDetails.phone_number = phoneNumber;
            }
            if (userDetails?.isCompany?.[0]) {
              userDetails.isCompany[0].name = this.businessName;
              userDetails.isCompany[0].details = this.businessDescription;
              userDetails.isCompany[0].address = this.businessDescription;
              if (userDetails.isCompany[0]._user) {
                userDetails.isCompany[0]._user.name = this.userName;
                if (phoneNumber) {
                  userDetails.isCompany[0]._user.phone_number = phoneNumber;
                }
              }
            }
            void this.secureStorage.setItem('userDetails', userDetails);
            // A sub-user's sidebar shows sub_user_name, which this save does not change.
            if (!userDetails?.is_sub_user) {
              this.sharedService.updateUserName(this.userName);
            }

            // Update local companyData immediately so UI stays in sync
            if (this.companyData) {
              this.companyData.name = this.businessName;
              this.companyData.details = this.businessDescription;
              this.companyData.address = this.businessDescription;
              this.companyData.description = this.businessDescription;
              this.companyData.sector_id = this.extractSectorIds(this.businessSector);
              this.companyData.business_sector = this.extractSectorIds(this.businessSector);
              if (this.companyData._user) {
                this.companyData._user.name = this.userName;
                if (phoneNumber) {
                  this.companyData._user.phone_number = phoneNumber;
                }
              }
            }

            // ✅ Preserve profile image in localStorage
            if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png') {
              localStorage.setItem('profileImage', this.imageSrc);
              this.sharedService.updateProfileImage(this.imageSrc);
            }

            this.originalBusinessDescription = this.businessDescription;
            this.originalBusinessSector = [...this.businessSector];
            this.isDescriptionChanged = false;
            this.profileForm.get('business_logo')?.markAsPristine();
            this.profileForm.markAsPristine();
            this.isEditMode = false;
            this.sharedService.createNotification('success', this.translateService.instant('profile-page.profile-update-success'));

            // Refresh company details
            this.getCompanyDetail();
          },
          error: (error) => {
            this.sharedService.hideLoader();
            console.error('Failed to update profile:', error);
            this.sharedService.createNotification('error', error?.error?.message || this.translateService.instant('profile-page.profile-update-failed'));
          },
        });
    } else {
      this.sharedService.hideLoader();
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.company-not-found'));
    }
  }
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  // Password change flow variables
  isExistingPasswordVerified = false;
  existingPasswordValue = '';
  isVerifyingPassword = false;


  // Initialize all password strength conditions as false
  initializePasswordStrength() {
    this.passwordStrength = {
      lowercase: false,
      uppercase: false,
      number: false,
      specialChar: false,
      minLength: false,
    };
  }

  // ✅ Improved getCompanyDetail to properly sync with backend

  async getCompanyDetail() {
    this.sharedService.showLoader();
    try {
      const response: any = await this.pagesService
        .getAll('GET_USER_COMPANY')
        .toPromise();

      if (response) {
        const userData = response[0] || {};
        this.companyData = userData;
        this.businessName = userData.name || 'Business Name';
        const arabicNameKey = this.arabicBusinessNameStorageKey;
        const savedArabicName = arabicNameKey ? this.secureStorage.getItem<string>(arabicNameKey) : null;
        this.businessNameArabic = typeof savedArabicName === 'string' ? savedArabicName : '';
        this.businessDescription =
          userData.details ||
          userData.description ||
          userData.business_description ||
          userData.businessDescription ||
          userData.address ||
          '';
        this.originalBusinessDescription = this.businessDescription;
        this.isDescriptionChanged = false;

        // Build sector cascader value from API response
        const sectorData = userData.business_sector || userData.sector_id || [];
        this.rawSectorData = sectorData;
        this.rememberSectorNames(sectorData);
        this.businessSector = this.buildSectorCascaderValue(sectorData);
        this.originalBusinessSector = [...this.businessSector];

        // ✅ Extract user data from _user object
        // Get phone number from multiple possible sources
        const userDetails = this.authService.getUserDetails() || {};
        this.userPhone =
          userData._user?.phone_number ||
          userData.user?.phone_number ||
          userData.phone_number ||
          userDetails?.phone_number ||
          userDetails?.phoneNumber ||
          userDetails?.isCompany?.[0]?._user?.phone_number ||
          userDetails?.isCompany?.[0]?.phone_number ||
          '';

        const resolvedUserName =
          userData._user?.name ||
          userData.user?.name ||
          userData.user_name ||
          userDetails?.name ||
          'User';
        const resolvedEmail =
          userData._user?.email ||
          userData.user?.email ||
          userData.email ||
          userDetails?.email ||
          '';

        this.userName = resolvedUserName;
        this.userEmail = resolvedEmail;
        if (!this.isEditMode) {
          this.editableName = this.userName;
          this.editablePhone = this.userPhone;
          this.editableBusinessName = this.businessName;
    this.editableBusinessNameArabic = this.businessNameArabic;
        }

        this.profileForm.patchValue({
          business_logo: userData.business_logo?.[0] || null,
          name: userData.name || '',
          address: userData.address || '',
          password: '',
        });

        // ✅ IMPROVED: Preserve image even when backend returns null
        if (userData.business_logo?.length && userData.business_logo[0].url) {
          // Backend has image - USE IT and SAVE IT
          this.imageSrc = userData.business_logo[0].url;
          localStorage.setItem('profileImage', this.imageSrc);
          this.sharedService.updateProfileImage(this.imageSrc);
        } else {
          // Backend returned null - check localStorage to preserve existing image
          const storedImage = localStorage.getItem('profileImage');

          if (storedImage && storedImage !== 'assets/images/profile.png' && !storedImage.includes('assets/images/profile')) {
            // Use existing stored image (preserve it)
            this.imageSrc = storedImage;
            this.sharedService.updateProfileImage(storedImage);
          } else {
            // No stored image, set default
            this.imageSrc = 'assets/images/profile.png';
          }
        }
      }
    } catch (error) {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.load-profile-failed')
      );
      console.error('Error loading user profile:', error);
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Load sectors from API
  loadSectors(): void {
    this.pagesService.getAll('GET_SECTORS').subscribe({
      next: (response: any) => {
        this.sectorsList = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];
        this.sectorNameMap.clear();
        this.sectorsList.forEach((item: any) => {
          if (item?.id !== null && item?.id !== undefined) {
            this.sectorNameMap.set(String(item.id), item?.name || String(item.id));
          }
        });
        this.rememberSectorNames(this.rawSectorData);
        this.sectorsTree = this.buildSectorTree(this.sectorsList);
        // The company may have loaded first, when there was no tree to build full paths from.
        if (this.rawSectorData && !this.isDescriptionChanged) {
          this.businessSector = this.buildSectorCascaderValue(this.rawSectorData);
          this.originalBusinessSector = [...this.businessSector];
        }
      },
      error: (err) => {
        console.error('Failed to load sectors', err);
      }
    });
  }

  buildSectorTree(flatList: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    // Only show active sectors — inactive ones must not appear in the settings page
    const activeList = flatList.filter((item: any) => item.is_active);

    activeList.forEach(item => {
      map.set(item.id, {
        value: item.id,
        label: item.name,
        children: [],
        isLeaf: true
      });
    });

    activeList.forEach(item => {
      const node = map.get(item.id);
      if (!item.parent_id || item.parent_id === 0) {
        roots.push(node);
      } else {
        const parent = map.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
          parent.isLeaf = false;
        }
      }
    });

    const cleanLeaves = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanLeaves(node.children);
        }
      });
    };
    cleanLeaves(roots);

    return roots;
  }

  // Extract leaf sector IDs from cascader value
  extractSectorIds(cascaderValue: any): number[] | null {
    if (!cascaderValue || !Array.isArray(cascaderValue) || cascaderValue.length === 0) {
      return null;
    }
    return cascaderValue.map((path: any): number => {
      if (Array.isArray(path) && path.length > 0) {
        return path[path.length - 1] as number;
      }
      return path as number;
    });
  }

  // Build cascader-compatible value from API sector data
  buildSectorCascaderValue(sectorData: any): any[] {
    if (!sectorData) return [];

    // If it's an array of IDs or objects
    let sectorIds: number[] = [];
    if (Array.isArray(sectorData)) {
      sectorIds = sectorData.map((s: any) => typeof s === 'object' ? (s.id || s.value) : s);
    } else if (typeof sectorData === 'number') {
      sectorIds = [sectorData];
    } else if (typeof sectorData === 'string' && sectorData) {
      // Try to parse if it's a JSON string
      try {
        const parsed = JSON.parse(sectorData);
        if (Array.isArray(parsed)) {
          sectorIds = parsed.map((s: any) => typeof s === 'object' ? (s.id || s.value) : s);
        }
      } catch {
        return [];
      }
    }

    if (sectorIds.length === 0) return [];

    // Build full paths for each sector ID
    return sectorIds
      .map(id => this.findSectorPath(id))
      .filter(path => path.length > 0);
  }

  // Find the full cascader path for a given sector ID
  findSectorPath(targetId: number): number[] {
    const findPath = (nodes: any[], currentPath: number[]): number[] | null => {
      for (const node of nodes) {
        const newPath = [...currentPath, node.value];
        if (node.value === targetId) {
          return newPath;
        }
        if (node.children && node.children.length > 0) {
          const found = findPath(node.children, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    return findPath(this.sectorsTree, []) || [targetId];
  }

  // Handle sector selection change
  onSectorChange(value: any): void {
    this.businessSector = value || [];
    this.onCompanyNameChange();
  }

  onSectorDropdownVisible(visible: boolean): void {
    if (!visible) return;
    const host = this.businessSectorCascader?.nativeElement;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    if (rect.width) {
      const widthPx = `${Math.round(rect.width)}px`;
      this.sectorMenuStyle = {
        width: widthPx,
        'min-width': widthPx
      };
    }
  }

  /** Names that arrive with the company's own sector objects, for sectors the list lacks. */
  private rememberSectorNames(sectorData: any): void {
    if (!Array.isArray(sectorData)) return;
    sectorData.forEach((s: any) => {
      if (!s || typeof s !== 'object') return;
      const id = s.id ?? s.value;
      const name = s.name ?? s.label;
      if (id !== null && id !== undefined && name && !this.sectorNameMap.has(String(id))) {
        this.sectorNameMap.set(String(id), name);
      }
    });
  }

  /**
   * The text the page shows for the SAVED sectors (the drawer edits a working copy). It must read
   * the same as the picker, so a value with no matching sector id (the API sometimes sends the
   * sector name itself) is shown as it came.
   */
  get businessSectorDisplay(): string {
    const values = Array.isArray(this.originalBusinessSector) ? this.originalBusinessSector : [];
    const names = values
      .map((path: any) => {
        const id = Array.isArray(path) ? path[path.length - 1] : path;
        if (id === null || id === undefined || id === '') return '';
        const known = this.sectorNameMap.get(String(id));
        if (known) return known;
        return typeof id === 'string' && !Number.isFinite(Number(id)) ? id : '';
      })
      .filter(Boolean);
    return names.join(', ');
  }

  /** A real uploaded picture, as opposed to the bundled placeholder. */
  get hasCustomAvatar(): boolean {
    const src = String(this.imageSrc || this.profileImage || '');
    return !!src && !src.includes('assets/images/profile.png') && !src.includes('assets/images/avatar.png');
  }

  get avatarSrc(): string {
    return this.imageSrc || this.profileImage || '';
  }

  /** First letter of the person's name, shown when there is no picture. */
  get userInitial(): string {
    const name = (this.isEditMode ? this.editableName : this.userName) || this.userName || '';
    return (Array.from(name.trim())[0] || '').toLocaleUpperCase();
  }

  createUserDetail(): FormGroup {
    return this.fb.group({
      addUser: [''],
      selectRole: [''],
    });
  }

  get userDetail(): FormArray {
    return this.profileForm.get('userDetail') as FormArray;
  }

  addUser() {
    this.userDetail.push(this.createUserDetail());
  }

  removeUser(index: number) {
    this.userDetail.removeAt(index);
  }

  onSubmit(): void {
    this.sharedService.showLoader();

    if (!this.profileForm.valid) {
      this.sharedService.hideLoader();
      this.profileForm.markAllAsTouched();
      return;
    }

    const businessLogoPayload = this.prepareBusinessLogoPayload();

    if (this.companyData?.id) {
      this.updateCompanyProfile(businessLogoPayload);
    } else {
      this.createCompanyProfile(businessLogoPayload);
    }
  }

  /**
   * Prepares the business logo payload based on current state
   */
  private prepareBusinessLogoPayload(): any {
    const logoValue = this.profileForm.get('business_logo')?.value;

    // Case 1: New image uploaded (base64 string from FileReader)
    if (this.isNewBase64Image(logoValue)) {
      return [{
        path: logoValue,
        name: this.currentImage?.name || 'uploaded-image.png',
        type: 'image',
        mime: this.currentImage?.type || 'image/png',
        size: this.currentImage?.size || 0,
        meta: 0,
      }];
    }

    // Case 2: Existing image object from backend
    if (this.isExistingImageObject(logoValue)) {
      return [logoValue];
    }

    // Case 3: Image was removed
    if (this.isImageRemoved()) {
      return null;
    }

    // Case 4: No change - preserve existing backend image
    return this.companyData?.business_logo || null;
  }

  /**
   * Checks if the logo value is a new base64 image
   */
  private isNewBase64Image(logoValue: any): boolean {
    return logoValue &&
      typeof logoValue === 'string' &&
      logoValue.startsWith('data:image');
  }

  /**
   * Checks if the logo value is an existing image object from backend
   */
  private isExistingImageObject(logoValue: any): boolean {
    return logoValue &&
      typeof logoValue === 'object' &&
      logoValue.url;
  }

  /**
   * Checks if the image was removed
   */
  private isImageRemoved(): boolean {
    return this.imageSrc === null ||
      this.imageSrc === 'assets/images/profile.png';
  }

  /**
   * Updates existing company profile
   */
  private updateCompanyProfile(businessLogoPayload: any): void {
    const payload = {
      business_logo: businessLogoPayload,
      name: this.profileForm.get('name')?.value,
      address: this.profileForm.get('address')?.value,
      company_as_client_0_id: this.companyData.id,
    };

    this.pagesService
      .patch('SET_COMPANY_DETAILS', this.companyData.id, payload)
      .subscribe({
        next: (response) => {
          this.handleUpdateSuccess();
        },
        error: (error) => {
          this.handleError('update', error);
        },
      });
  }

  /**
   * Creates new company profile
   */
  private createCompanyProfile(businessLogoPayload: any): void {
    const payload = {
      business_logo: businessLogoPayload,
      name: this.profileForm.get('name')?.value,
      details: this.profileForm.get('address')?.value,
    };

    this.pagesService
      .update('SET_COMPANY_DETAILS', payload)
      .subscribe({
        next: (response) => {
          this.handleCreateSuccess();
        },
        error: (error) => {
          this.handleError('create', error);
        },
      });
  }

  /**
   * Handles successful update response
   */
  private handleUpdateSuccess(): void {
    this.sharedService.hideLoader();

    // ✅ Preserve uploaded image in localStorage before refreshing
    if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png' && !this.imageSrc.includes('assets/images/profile')) {
      localStorage.setItem('profileImage', this.imageSrc);
      this.sharedService.updateProfileImage(this.imageSrc);
    }

    this.sharedService.createNotification(
      'success',
      this.translateService.instant('profile-page.profile-updated-msg')
    );
    this.getCompanyDetail();
  }

  /**
   * Handles successful create response
   */
  private handleCreateSuccess(): void {
    this.sharedService.hideLoader();

    // ✅ Preserve uploaded image in localStorage before refreshing
    if (this.imageSrc && this.imageSrc !== 'assets/images/profile.png' && !this.imageSrc.includes('assets/images/profile')) {
      localStorage.setItem('profileImage', this.imageSrc);
      this.sharedService.updateProfileImage(this.imageSrc);
    }

    this.sharedService.createNotification(
      'success',
      this.translateService.instant('profile-page.profile-created-msg')
    );
    this.getCompanyDetail();
    this.clearCompanyAlert();
  }

  /**
   * Clears company-related alerts from localStorage
   */
  private clearCompanyAlert(): void {
    const alertsJson = localStorage.getItem('app_alerts');
    if (!alertsJson) return;

    try {
      let alerts = JSON.parse(alertsJson);
      alerts = alerts.filter((alert: any) => alert.action !== 'company');
      localStorage.setItem('app_alerts', JSON.stringify(alerts));
      this.sharedService.closeAlertByAction('company');
    } catch (error) {
      console.error('Error clearing company alerts:', error);
    }
  }

  /**
   * Handles error responses
   */
  private handleError(operation: 'create' | 'update', error: any): void {
    this.sharedService.hideLoader();

    // Only show error notification for create operation
    if (operation === 'create') {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.profile-update-failed')
      );
    }

    console.error(`Failed to ${operation} company profile:`, error);
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.files && input.files[0]) {
      if (!this.isAcceptableImage(input.files[0])) {
        input.value = '';
        return;
      }

      // Open cropper modal
      this.droppedImageFile = undefined;
      this.imageChangedEvent = event;
      this.cropperModalVisible = true;
    }
  }

  onPhotoDragOver(event: DragEvent) {
    event.preventDefault();
    this.isPhotoDragOver = true;
  }

  onPhotoDrop(event: DragEvent) {
    event.preventDefault();
    this.isPhotoDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file || !this.isAcceptableImage(file)) return;

    this.imageChangedEvent = null;
    this.droppedImageFile = file;
    this.cropperModalVisible = true;
  }

  /** JPG or PNG, at most 5 MB. Shows the reason when the file is turned down. */
  private isAcceptableImage(file: File): boolean {
    const profileControl = this.profileForm.get('business_logo');
    profileControl?.setErrors(null);

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      profileControl?.setErrors({ fileType: true });
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.image-type-invalid')
      );
      return false;
    }

    const maxFileSize = 5 * 1024 * 1024;
    if (file.size > maxFileSize) {
      profileControl?.setErrors({ fileSize: true });
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.image-size-invalid')
      );
      return false;
    }
    return true;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.blob ? URL.createObjectURL(event.blob) : '';
    // Convert blob to base64 for form submission
    if (event.blob) {
      const reader = new FileReader();
      reader.onload = () => {
        this.croppedImage = reader.result as string;
      };
      reader.readAsDataURL(event.blob);
    }
  }

  imageLoaded(image: LoadedImage) {
    // Image loaded in cropper
  }

  cropperReady() {
    // Cropper ready
  }

  loadImageFailed() {
    this.sharedService.createNotification('error', this.translateService.instant('profile-page.image-load-failed'));
    this.cropperModalVisible = false;
    this.droppedImageFile = undefined;
  }

  confirmCrop() {
    if (this.croppedImage) {
      const profileControl = this.profileForm.get('business_logo');
      this.imageSrc = this.croppedImage;
      profileControl?.setValue(this.croppedImage);
      profileControl?.markAsDirty();
      this.profileForm.markAsDirty();
    }
    this.cropperModalVisible = false;
    this.imageChangedEvent = null;
    this.droppedImageFile = undefined;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  cancelCrop() {
    this.cropperModalVisible = false;
    this.imageChangedEvent = null;
    this.droppedImageFile = undefined;
    this.croppedImage = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  resetPassword = {
    old: false,
    new: false,
    confirm: false,
  };

  toggleResetPasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
    this.resetPassword[field] = !this.resetPassword[field];
  }

  removeImage(): void {
    this.imageSrc = null;
    this.currentImage = null;
    this.profileImage = null;

    const profileControl = this.profileForm.get('business_logo');
    if (profileControl) {
      profileControl.setValue(null);
      profileControl.setErrors(null);
      profileControl.markAsDirty();
    }
    this.profileForm.markAsDirty();

    // localStorage clear
    localStorage.removeItem('profileImage');

    // default image set in shared service (isse subscription trigger hoga)
    this.sharedService.updateProfileImage('assets/images/profile.png');

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }



  /** Passwords may not contain spaces: typed or pasted spaces are dropped as they arrive. */
  stripPasswordSpaces(controlName: 'new_password' | 'confirmPassword'): void {
    const control = this.resetPasswordForm.controls[controlName];
    const value: string = control.value || '';
    if (/\s/.test(value)) {
      control.setValue(value.replace(/\s/g, ''));
    }
  }

  /** True once a new password is typed that matches the verified existing one. */
  get isNewPasswordSameAsOld(): boolean {
    const newPassword = this.resetPasswordForm.get('new_password')?.value;
    return !!newPassword && this.isExistingPasswordVerified && newPassword === this.existingPasswordValue;
  }

  // ✅ Real-time password strength validation
  onPasswordInput(): void {
    this.stripPasswordSpaces('new_password');
    const password = this.resetPasswordForm.controls['new_password'].value || '';

    this.passwordStrength.lowercase = /[a-z]/.test(password);
    this.passwordStrength.uppercase = /[A-Z]/.test(password);
    this.passwordStrength.number = /\d/.test(password);
    this.passwordStrength.specialChar = /[^A-Za-z0-9\s]/.test(password);
    this.passwordStrength.minLength = password.length >= 8;

    const allPassed = Object.values(this.passwordStrength).every(Boolean);
    const passwordControl = this.resetPasswordForm.controls['new_password'];

    if (!allPassed) {
      passwordControl.setErrors({ passwordWeak: true });
    } else {
      if (passwordControl.hasError('passwordWeak')) {
        passwordControl.setErrors(null);
      }
    }
  }

  // ✅ Enhanced canSubmit with all validations
  canSubmit(): boolean {
    const allPassed = Object.values(this.passwordStrength).every(Boolean);
    const formValid = this.resetPasswordForm.valid;

    // ✅ Check if old and new passwords are different
    const oldPassword = this.existingPasswordValue;
    const newPassword = this.resetPasswordForm.get('new_password')?.value;
    const passwordsDifferent = oldPassword !== newPassword;

    return formValid && allPassed && passwordsDifferent && this.isExistingPasswordVerified;
  }

  onResetPassword() {
    this.sharedService.showLoader();

    // ✅ Check if new password is the same as current password
    const oldPassword = this.existingPasswordValue;
    const newPassword = this.resetPasswordForm.get('new_password')?.value;

    if (oldPassword === newPassword) {
      this.sharedService.hideLoader();
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('profile-page.new-password-same')
      );
      return;
    }

    if (this.canSubmit()) {
      const userDetails = this.authService.getUserDetails() || {};
      const email = userDetails?.email || '';

      const payload = {
        old_password: oldPassword,
        new_password: this.resetPasswordForm.value.new_password,
        user_email: email,
      };

      this.pagesService
        .resetPassword('USER_RESET_PASSWORD', payload)
        .subscribe({
          next: (response: any) => {
            this.sharedService.hideLoader();

            const output = response?.output;

            if (output?.isSuccess) {
              this.sharedService.createNotification(
                'success',
                output?.message || this.translateService.instant('profile-page.password-reset-success')
              );

              // Reset form and password strength
              this.resetPasswordFlow();

              setTimeout(() => {
                this.secureStorage.clearProtected();
                sessionStorage.removeItem('jwtToken');

                this.sharedService.createNotification(
                  'info',
                  this.translateService.instant('profile-page.login-new-password')
                );

                this.router.navigate(['/auth/login']);
              }, 2000);
            } else {
              // Show backend error message (e.g., incorrect existing password)
              this.sharedService.createNotification(
                'error',
                output?.message || this.translateService.instant('profile-page.password-reset-failed')
              );
            }
          },
          error: (error) => {
            this.sharedService.hideLoader();

            // Show backend error message
            const errorMessage = error?.error?.output?.message || error?.error?.message || this.translateService.instant('profile-page.password-reset-failed');
            this.sharedService.createNotification('error', errorMessage);
          },
        });
    } else {
      this.sharedService.hideLoader();
      this.resetPasswordForm.markAllAsTouched();
    }
  }

  // ✅ Custom validator for password matching
  passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('new_password')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  // Verify existing password with backend
  verifyExistingPassword() {
    if (!this.existingPasswordValue.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('profile-page.enter-existing-password'));
      return;
    }

    this.isVerifyingPassword = true;
    this.sharedService.showLoader();

    const userDetails = this.authService.getUserDetails() || {};
    const email = userDetails?.email || '';

    // Send same password to verify without actually changing it
    const payload = {
      old_password: this.existingPasswordValue,
      new_password: this.existingPasswordValue, // Same password
      user_email: email,
    };

    this.pagesService
      .resetPassword('USER_RESET_PASSWORD', payload)
      .subscribe({
        next: (response: any) => {
          this.isVerifyingPassword = false;
          this.sharedService.hideLoader();

          const output = response?.output;

          // Check if response indicates old password was correct
          // Even if backend says "same password" error, we know old password is right
          if (output?.isSuccess === false) {
            // Check if error is about same password (means old password was correct)
            const message = output?.message?.toLowerCase() || '';
            if (message.includes('same') || message.includes('different')) {
              // Old password was correct, show next fields
              this.isExistingPasswordVerified = true;
              this.resetPasswordForm.patchValue({
                oldPassword: this.existingPasswordValue
              });
            } else {
              // Old password was wrong
              this.sharedService.createNotification('error', output?.message || this.translateService.instant('profile-page.incorrect-existing-password'));
              this.isExistingPasswordVerified = false;
            }
          } else {
            // Success means old password was correct
            this.isExistingPasswordVerified = true;
            this.resetPasswordForm.patchValue({
              oldPassword: this.existingPasswordValue
            });
          }
        },
        error: (error) => {
          this.isVerifyingPassword = false;
          this.sharedService.hideLoader();

          // Check error message from backend
          const errorMessage = error?.error?.output?.message || error?.error?.message || '';
          const messageLower = errorMessage.toLowerCase();

          // If error is about same password, old password was correct
          if (messageLower.includes('same') || messageLower.includes('different')) {
            this.isExistingPasswordVerified = true;
            this.resetPasswordForm.patchValue({
              oldPassword: this.existingPasswordValue
            });
          } else {
            // Old password is wrong
            this.sharedService.createNotification('error', errorMessage || this.translateService.instant('profile-page.incorrect-existing-password'));
            this.isExistingPasswordVerified = false;
          }
        },
      });
  }

  // Reset password flow
  resetPasswordFlow() {
    this.isExistingPasswordVerified = false;
    this.existingPasswordValue = '';
    this.resetPasswordForm.reset();
    this.initializePasswordStrength();
  }

  // Image Modal Methods
  openImageModal(imageUrl: string): void {
    if (imageUrl) {
      this.imageModalImages = [imageUrl];
      this.imageModalCurrentIndex = 0;
      this.imageModalVisible = true;
    }
  }

  closeImageModal(): void {
    this.imageModalVisible = false;
    this.imageModalImages = [];
    this.imageModalCurrentIndex = 0;
  }

  onImageIndexChange(index: number): void {
    this.imageModalCurrentIndex = index;
  }

  hasUnsavedChanges(): boolean {
    if (!this.isEditMode) return false;
    return (
      !!this.profileForm?.dirty ||
      this.isDescriptionChanged ||
      this.editableName.trim() !== this.userName.trim() ||
      this.editableBusinessName.trim() !== this.businessName.trim() ||
      this.normalizePhone(this.editablePhone).replace(/^\+/, '') !== this.normalizePhone(this.userPhone).replace(/^\+/, '')
    );
  }

  // Set once the user has already confirmed a refresh in our own modal, so the browser's
  // "Reload site?" prompt doesn't ask the same question a second time.
  private reloadConfirmed = false;

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.reloadConfirmed && this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isRefresh = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'r');
    if (isRefresh && this.hasUnsavedChanges()) {
      event.preventDefault();
      this.modal.confirm({
        nzTitle: this.translateService.instant('profile-page.unsaved-title'),
        nzContent: this.translateService.instant('profile-page.unsaved-refresh-content'),
        nzOkText: this.translateService.instant('profile-page.yes-refresh'),
        nzCancelText: this.translateService.instant('common.cancel'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOnOk: () => {
          this.reloadConfirmed = true;
          window.location.reload();
        },
        nzOnCancel: () => {}
      });
    }
  }

  confirmUnsavedChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.confirm({
        nzTitle: this.translateService.instant('profile-page.unsaved-title'),
        nzContent: this.translateService.instant('profile-page.unsaved-leave-content'),
        nzOkText: this.translateService.instant('profile-page.yes-leave'),
        nzCancelText: this.translateService.instant('common.cancel'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false)
      });
    });
  }

}
